import React, { useMemo, useRef } from 'react';
import { useEyeconxStore } from '../../store/eyeconxStore';
import {
    MAX_SMS_LENGTH,
    SMS_KEYWORDS,
    SMS_SEGMENT_LENGTH,
    buildSmsPreviewValues,
    getSmsSegmentCount,
    insertKeywordAtCursor,
    replaceSmsKeywords,
} from './smsPreviewUtils';
import './MessageContentStep.css';

function MessageContentStep({
    formData,
    setFormData,
    maxLength = MAX_SMS_LENGTH,
    label = 'SMS Message',
    placeholder = 'Write your SMS message. Click a keyword below to personalize it.',
}) {
    const textareaRef = useRef(null);
    const clinicInfo = useEyeconxStore((state) => state.clinicInfo);
    const messageContent = formData.messageContent || '';
    const charCount = messageContent.length;
    const segmentCount = getSmsSegmentCount(charCount);

    const previewValues = useMemo(() => buildSmsPreviewValues(clinicInfo), [clinicInfo]);
    const previewMessage = useMemo(
        () => replaceSmsKeywords(messageContent, previewValues),
        [messageContent, previewValues]
    );

    const patientKeywords = SMS_KEYWORDS.filter((item) => item.group === 'Patient');
    const clinicKeywords = SMS_KEYWORDS.filter((item) => item.group === 'Clinic');

    const updateMessage = (nextValue) => {
        setFormData({
            ...formData,
            messageContent: nextValue.slice(0, maxLength),
        });
    };

    const handleInsertKeyword = (keyword) => {
        const textarea = textareaRef.current;
        const selectionStart = textarea?.selectionStart ?? messageContent.length;
        const selectionEnd = textarea?.selectionEnd ?? selectionStart;
        const { nextValue, nextCursor } = insertKeywordAtCursor(
            messageContent,
            keyword,
            selectionStart,
            selectionEnd
        );

        const clippedValue = nextValue.slice(0, maxLength);
        const clippedCursor = Math.min(nextCursor, clippedValue.length);

        updateMessage(clippedValue);

        requestAnimationFrame(() => {
            if (!textareaRef.current) {
                return;
            }
            textareaRef.current.focus();
            textareaRef.current.setSelectionRange(clippedCursor, clippedCursor);
        });
    };

    return (
        <div className="animate-fade-in ecx-sms-compose">
                <h3 className="fw-normal text-dark mb-2">Message Content</h3>
            <p className="text-muted mb-6">
                Write your SMS and insert keywords to personalize each message. Preview updates live with sample patient data.
            </p>

            <div className="row g-6">
                <div className="col-lg-7">
                    <div className="mb-4">
                        <label className="form-label fw-normal" htmlFor="sms-blast-message">
                            {label}
                        </label>
                        <textarea
                            id="sms-blast-message"
                            ref={textareaRef}
                            className="form-control form-control-lg ecx-sms-compose__textarea"
                            rows="8"
                            placeholder={placeholder}
                            maxLength={maxLength}
                            value={messageContent}
                            onChange={(e) => updateMessage(e.target.value)}
                        />
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-2">
                            <span className="text-muted fs-7">
                                {segmentCount === 0
                                    ? '0 SMS segments'
                                    : `${segmentCount} SMS segment${segmentCount === 1 ? '' : 's'} (~${SMS_SEGMENT_LENGTH} chars each)`}
                            </span>
                            <span className={`fs-7 fw-normal ${charCount >= maxLength ? 'text-danger' : 'text-muted'}`}>
                                {charCount}/{maxLength}
                            </span>
                        </div>
                    </div>

                    <div className="ecx-sms-compose__keywords mb-4">
                        <div className="ecx-sms-compose__keywords-title">Keywords</div>
                        <p className="text-muted fs-7 mb-3">
                            Click a keyword to insert it at the cursor. Keywords are replaced per patient when the blast is sent.
                        </p>

                        <div className="ecx-sms-compose__keyword-group mb-3">
                            <span className="ecx-sms-compose__keyword-group-label">Patient</span>
                            <div className="ecx-sms-compose__keyword-chips">
                                {patientKeywords.map((item) => (
                                    <button
                                        key={item.key}
                                        type="button"
                                        className="ecx-sms-compose__keyword-chip"
                                        onClick={() => handleInsertKeyword(item.key)}
                                        title={`Insert {${item.key}}`}
                                    >
                                        {`{${item.key}}`}
                                        <span>{item.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="ecx-sms-compose__keyword-group">
                            <span className="ecx-sms-compose__keyword-group-label">Clinic</span>
                            <div className="ecx-sms-compose__keyword-chips">
                                {clinicKeywords.map((item) => (
                                    <button
                                        key={item.key}
                                        type="button"
                                        className="ecx-sms-compose__keyword-chip"
                                        onClick={() => handleInsertKeyword(item.key)}
                                        title={`Insert {${item.key}}`}
                                    >
                                        {`{${item.key}}`}
                                        <span>{item.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="alert alert-light border d-flex align-items-start gap-3 mb-0">
                        <span className="ecx-page-icon-badge flex-shrink-0">
                            <i className="bi bi-info-circle" aria-hidden="true" />
                        </span>
                        <div className="d-flex flex-column">
                            <span className="fw-normal text-dark">Tip</span>
                            <span className="text-muted">
                                Keep messages under 160 characters for a single SMS segment. Keywords count toward the limit
                                using their placeholder text until replaced.
                            </span>
                        </div>
                    </div>
                </div>

                <div className="col-lg-5">
                    <div className="ecx-sms-compose__preview">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <h6 className="mb-0 fw-normal">Sample preview</h6>
                            <span className="badge bg-light text-muted">Live</span>
                        </div>
                        <p className="text-muted fs-7 mb-4">
                            Showing sample patient values
                            {clinicInfo?.name ? ` and ${clinicInfo.name}` : ' and your clinic details'}.
                        </p>

                        <div className="ecx-sms-compose__phone">
                            <div className="ecx-sms-compose__phone-notch" />
                            <div className="ecx-sms-compose__phone-screen">
                                <div className="ecx-sms-compose__phone-header">
                                    <span className="ecx-sms-compose__phone-contact">
                                        {previewValues.PATIENT_NAME}
                                    </span>
                                    <span className="ecx-sms-compose__phone-number">Sample patient</span>
                                </div>
                                <div className="ecx-sms-compose__phone-body">
                                    {previewMessage ? (
                                        <div className="ecx-sms-compose__message-bubble">{previewMessage}</div>
                                    ) : (
                                        <p className="ecx-sms-compose__empty mb-0">
                                            Your SMS preview will appear here as you type.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MessageContentStep;
